/**
 * Daily movement report — today's minutes, everyone's running totals, and a
 * one-tap "send it to me now". Runs itself every evening at 9pm ET.
 */
import { useCallback, useEffect, useState } from "react";
import { EnvelopeSimple, ArrowClockwise, Eye } from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const num = (n) => Number(n || 0).toLocaleString();

export default function DailyReportPanel({ token }) {
  const [d, setD] = useState(null);  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");

  const headers = { "X-Admin-Token": token };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/admin/daily-report/preview`, { headers });
      setD(await r.json());
    } catch (e) {
      setD({ error: e?.message });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setSending(true); setMsg("");
    try {
      const r = await fetch(`${BACKEND}/api/admin/daily-report/send?force=true`, { method: "POST", headers });
      const j = await r.json();
      if (!r.ok || !j.sent) throw new Error(j.detail || "Send failed — check the Resend key");
      setMsg(`Sent to ${j.to}`);
      load();
    } catch (e) {
      setMsg(e?.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <CollapsibleSection
      id="daily-report"
      testId="daily-report-panel"
      title="Daily movement report"
      icon={<EnvelopeSimple size={20} weight="bold" className="text-[#FF1B91]" />}
      subtitle="Emailed to you 8am, 12pm and 8pm ET — the full leaderboard with everyone's minutes."      onRefresh={load}
      defaultOpen
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="send-daily-report-btn"
            onClick={send}
            disabled={sending}
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-[#FF1B91] text-white bg-[#FF1B91]/10 hover:bg-[#FF1B91]/25 disabled:opacity-40 px-4 py-2"
          >
            <EnvelopeSimple size={12} weight="bold" /> {sending ? "Sending…" : "Send it to me now"}
          </button>
          <a
            data-testid="preview-daily-email-link"
            href={`${BACKEND}/api/admin/daily-report/html`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/25 text-white hover:border-[#FF1B91] px-4 py-2"
          >
            <Eye size={12} weight="bold" /> Preview email
          </a>
          <button
            type="button"
            data-testid="reload-daily-report-btn"
            onClick={load}
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/45 hover:text-[#FF1B91]"
          >
            <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          {d?.last_sent_at && (
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">
              Last sent {new Date(d.last_sent_at).toLocaleString()}{d.last_recipient ? ` → ${d.last_recipient}` : ""}
            </span>
          )}
          {msg && <span className="text-[11px] text-emerald-300" data-testid="daily-report-msg">{msg}</span>}
        </div>

        {d && !d.error && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 max-w-4xl" data-testid="daily-report-stats">
              {[
                ["Minutes today", num(d.minutes_today), `${num(d.movers_today)} people moved`],
                ["Total so far", num(d.total_minutes), `of ${num(d.total_goal)} · ${d.pct_complete}%`],
                ["Finished 911", num(d.finished_count), `${num(d.yet_to_start)} yet to start`],
                ["Participants", num(d.total_registered), d.new_today?.length ? `${d.new_today.length} joined today` : "no new today"],
                ["Days left", num(d.days_left), "of the challenge"],
              ].map(([label, value, sub]) => (
                <div key={label} className="border border-white/12 bg-black/40 p-3">
                  <div className="text-[9px] uppercase tracking-[0.25em] font-bold text-white/45">{label}</div>
                  <div className="font-black text-2xl mt-1 text-white leading-none">{value}</div>
                  <div className="text-[10px] text-white/40 mt-1.5">{sub}</div>
                </div>
              ))}
            </div>
            <p className="text-white/35 text-[11px]">
              Every participant&rsquo;s minutes now live in the <span className="text-white/60">Participants</span> panel
              above — searchable and sortable. This panel just controls the email.
            </p>
          </>
        )}
        {d?.error && <div className="text-[11px] text-[#FF1B91]">{d.error}</div>}
      </div>
    </CollapsibleSection>
  );
}
